import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_CONFIG } from '../../config/mapbox';

// Set the Mapbox access token
mapboxgl.accessToken = MAPBOX_CONFIG.accessToken;

const ServiceRadiusMap = ({ address, radius = 10, height = '250px' }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const radiusCircle = useRef(null);
  const marker = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize the map when the component mounts
  useEffect(() => {
    if (!address || !address.coordinates) return;

    const { latitude, longitude } = address.coordinates;
    
    if (map.current) return; // Map already initialized

    // Create new map instance
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAPBOX_CONFIG.defaultMapStyle,
      center: [longitude, latitude],
      zoom: MAPBOX_CONFIG.defaultZoom
    });

    // Add controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Set flag when map is loaded
    map.current.on('load', () => {
      setMapLoaded(true);
    });

    // Clean up the map when the component unmounts
    return () => {
      if (marker.current) marker.current.remove();
      if (map.current) map.current.remove();
      map.current = null;
      marker.current = null;
      radiusCircle.current = null;
    };
  }, [address]);

  // Update marker and radius circle when address or radius changes
  useEffect(() => {
    if (!address || !address.coordinates || !map.current || !mapLoaded) return;

    const { latitude, longitude } = address.coordinates;

    // Center the map on the address location
    map.current.setCenter([longitude, latitude]);

    // Update or create marker
    if (marker.current) {
      marker.current.setLngLat([longitude, latitude]);
    } else {
      // Create a DOM element for the marker
      const el = document.createElement('div');
      el.className = 'marker';
      el.style.backgroundColor = MAPBOX_CONFIG.markerColor;
      el.style.width = '20px';
      el.style.height = '20px';
      el.style.borderRadius = '50%';
      el.style.border = '2px solid white';
      el.style.boxShadow = '0 0 0 2px rgba(6, 148, 162, 0.4)';

      marker.current = new mapboxgl.Marker(el)
        .setLngLat([longitude, latitude])
        .addTo(map.current);
    }

    // Add or update the radius circle
    const radiusInMeters = radius * 1609.34; // Convert miles to meters
    
    if (map.current.getSource('radius-circle')) {
      // Update existing source
      map.current.getSource('radius-circle').setData({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude]
        },
        properties: {
          radius: radiusInMeters
        }
      });
    } else if (mapLoaded) {
      // Add new source and layer
      map.current.addSource('radius-circle', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [longitude, latitude]
          },
          properties: {
            radius: radiusInMeters
          }
        }
      });

      map.current.addLayer({
        id: 'radius-circle',
        type: 'circle',
        source: 'radius-circle',
        paint: {
          'circle-radius': {
            stops: [
              [0, 0],
              [20, radiusInMeters / 3]
            ],
            base: 2
          },
          'circle-color': MAPBOX_CONFIG.markerColor,
          'circle-opacity': 0.2,
          'circle-stroke-width': 2,
          'circle-stroke-color': MAPBOX_CONFIG.markerColor,
          'circle-stroke-opacity': 0.5
        }
      });
    }

    // Adjust the map's zoom to show the entire radius circle
    if (mapLoaded) {
      const zoom = calculateZoomForRadius(radiusInMeters);
      map.current.setZoom(zoom);
    }
  }, [address, radius, mapLoaded]);

  // Calculate the appropriate zoom level for a given radius in meters
  const calculateZoomForRadius = (radiusInMeters) => {
    // These values are approximate and may need adjustment
    const metersPerPixel = radiusInMeters / 200; // Want the radius to be about 200px on screen
    const zoomLevel = Math.log2(40075016.686 / (metersPerPixel * 256)) - 1;
    return Math.max(Math.min(zoomLevel, 18), 5); // Clamp between zoom 5 and 18
  };

  return (
    <div 
      ref={mapContainer} 
      className="service-radius-map rounded-lg overflow-hidden" 
      style={{ height, width: '100%' }}
    />
  );
};

export default ServiceRadiusMap; 